import { SharePayload } from './protocol-types';
import { decodeBase64Url } from './base64url';
import { verifyChecksum } from './checksum';
import { validateVersion } from './protocol-version';
import { ProtocolValidationError, ProtocolChecksumError } from './protocol-errors';

import { SharePayloadSchema } from '../../shared/validation/protocol.schema';

function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

export function parsePayload(serializedPayload: string): SharePayload {
  try {
    const packet = decodeBase64Url(serializedPayload);
    
    if (packet.length !== 64) {
      throw new Error(`Invalid packet size: expected 64 bytes, received ${packet.length} bytes`);
    }
    
    const view = new DataView(packet.buffer, packet.byteOffset, packet.byteLength);
    
    const version = view.getUint8(0);
    const flags = view.getUint8(1);
    
    validateVersion(version);
    
    const checksumTarget = packet.subarray(0, 60);
    const expectedChecksum = view.getUint32(60, true);
    
    if (!verifyChecksum(checksumTarget, expectedChecksum)) {
      throw new ProtocolChecksumError(
        'Payload checksum verification failed. The share link may be corrupted.'
      );
    }
    
    // Bytes 4-19: Share ID (16 bytes)
    const shareId = bytesToHex(packet.subarray(4, 20));
    // Bytes 20-39: Info Hash (20 bytes)
    const infoHash = bytesToHex(packet.subarray(20, 40));
    // Bytes 40-59: Manifest Hash (20 bytes)
    const manifestHash = bytesToHex(packet.subarray(40, 60));
    
    const cleanInfoHash = infoHash === '0000000000000000000000000000000000000000' ? '' : infoHash;
    const cleanManifestHash = manifestHash === '0000000000000000000000000000000000000000' ? '' : manifestHash;
    
    const raw = {
      version,
      shareId,
      infoHash: cleanInfoHash,
      manifestHash: cleanManifestHash,
      flags,
      checksum: expectedChecksum,
    };

    // Validate at trust boundary — this is untrusted input from QR/URL
    if (SharePayloadSchema) {
      const result = SharePayloadSchema.safeParse(raw);
      if (!result.success) {
        throw new ProtocolValidationError(
          `Share payload validation failed: ${result.error.message}`
        );
      }
      return result.data as SharePayload;
    }

    return raw;
  } catch (err) {
    if (err instanceof ProtocolValidationError || err instanceof ProtocolChecksumError) {
      throw err;
    }
    throw new Error(
      `Failed to parse share payload: ${err instanceof Error ? err.message : String(err)}`
    );
  }
}
